import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../i18n/I18nContext';
import { getGuestPaymentStatus, getPaymentStatus } from '../services/api';
import { PaymentStatusResponse } from '../types/api';

export const PaymentSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const paymentId = Number(searchParams.get('paymentId') || 0);
  const movieId = Number(searchParams.get('movieId') || 0);
  const isGuest = searchParams.get('guest') === '1';
  const accessToken = searchParams.get('accessToken') || '';
  const [payment, setPayment] = useState<PaymentStatusResponse | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState('');

  const watchUrl = useMemo(() => `/watch/${payment?.payment.movieId || movieId}`, [movieId, payment?.payment.movieId]);

  useEffect(() => {
    if (!paymentId || (isGuest && !accessToken)) {
      setError(t('paymentReferenceMissing'));
      return;
    }

    let active = true;
    const load = async () => {
      try {
        const response = isGuest ? await getGuestPaymentStatus(paymentId, accessToken) : await getPaymentStatus(paymentId);
        if (!active) return;
        setPayment(response);
        if (response.payment.status === 'succeeded' && response.rental) {
          if (!isGuest) {
            window.setTimeout(() => navigate(watchUrl, { replace: true }), 1200);
          }
          return;
        }
        setAttempts((current) => current + 1);
      } catch {
        if (active) setError(t('paymentStatusError'));
      }
    };

    load();
    const timer = window.setInterval(load, 2500);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [accessToken, isGuest, navigate, paymentId, t, watchUrl]);

  const isReady = payment?.payment.status === 'succeeded' && payment.rental;
  const isWaiting = payment?.payment.status === 'pending' || (!payment && !error);

  return (
    <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="rounded-lg bg-stone-300 p-6 text-center shadow-sm">
        <h1 className="text-3xl font-bold text-stone-950">{t('paymentReceived')}</h1>
        {isReady && (
          <>
            <p className="mt-3 text-stone-700">{isGuest ? t('guestRentalEmailSent', { email: payment.payment.guestEmail || '' }) : t('rentalActiveOpening')}</p>
            {!isGuest && (
              <Link className="mt-6 inline-block rounded-md bg-[#ffd460] px-5 py-3 font-semibold text-stone-950 hover:bg-[#ffc83d]" to={watchUrl}>
                {t('watchNow')}
              </Link>
            )}
          </>
        )}
        {isWaiting && (
          <>
            <p className="mt-3 text-stone-700">{t('confirmingPayment')}</p>
            <p className="mt-2 text-sm text-stone-500">{t('webhookDelay')}</p>
            {attempts > 8 && <p className="mt-4 rounded-md bg-[#fff3c4] p-3 text-sm text-stone-700">{t('webhookStillWaiting')}</p>}
          </>
        )}
        {payment?.payment.status === 'failed' && <p className="mt-4 rounded-md bg-red-50 p-3 text-red-700">{t('paymentFailed')}</p>}
        {error && <p className="mt-4 rounded-md bg-red-50 p-3 text-red-700">{error}</p>}
        <Link className="mt-6 block text-sm font-semibold text-stone-700" to="/">
          {t('backToCatalog')}
        </Link>
      </div>
    </main>
  );
};
